import { useMemo, useState } from "react";
import * as React from "react";
import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { ArrowsDownUp, CaretDown, CaretRight, PencilSimple, Trash } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { isFactorRequiredCategory } from "./const";
import { resolveKwhMeasureLabel } from "./helpers";
import {
  activeDimensionCount,
  renderDimensionHeaderCells,
  renderDimensionValueCells,
} from "./dimensionDisplay";
import { useV2TableFilters } from "./useV2TableFilters";
import DimensionsGridFilterBar from "../DimensionsGrid/DimensionsGridFilterBar";
import { Paginator } from "../../../../components/Paginator";
import RowDetails from "./RowDetails";

const TH_PROPS = {
  py: 3,
  px: 4,
  fontSize: "13px",
  color: "gray.600",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "normal",
};

const isExpandable = (category) =>
  category === "CERTIFIED_RENEWABLE" || isFactorRequiredCategory(category);

/**
 * Presentational items table for purchased electricity. Rows for the three
 * factor-bearing categories (Supplier Contracted / Direct-Private Wire /
 * Certified Renewable) show an expand caret revealing RowDetails. A lime
 * totals band, grouped by measure, closes the table. Edit/delete actions are
 * hidden in preview (read-only) mode.
 *
 * `rows` are the raw API records (dimensionItems nested, category/consumption
 * at top level). A shared hook flattens/decorates them and provides the
 * dimension + category filter bar and client-side pagination (matching the
 * Scope 1/3 task tables).
 */
function PurchasedElectricityTable({ organizationStructure, rows, isPreview, onEdit, onDelete }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const dimCount = activeDimensionCount(organizationStructure);
  const measureLabel = resolveKwhMeasureLabel(organizationStructure);

  const filterColumns = useMemo(
    () => [
      {
        key: "category",
        label: t("columns.category"),
        getValue: (r) => (r.category ? t(`categories.${r.category}`) : ""),
      },
    ],
    [t]
  );

  const {
    dimensionGroups,
    extraFilterColumns,
    filters,
    handleFilterChange,
    clearAllFilters,
    filteredItems,
    pageSlice,
    totalPages,
    safePageIndex,
    pageSize,
    handlePageSelection,
    handlePageSizeChange,
    totalCount,
    isFiltered,
  } = useV2TableFilters(organizationStructure, rows, filterColumns);

  // Group totals by measure unit over the filtered set so mixed units stay
  // separate. Scope 2 v2 is kWh-only today, but the grouping mirrors the rest
  // of the app's footers.
  const totalsByMeasure = filteredItems.reduce((acc, r) => {
    const key = r.consumption?.measureId ?? "kwh";
    const n = Number(r.consumption?.value);
    if (!Number.isFinite(n)) return acc;
    acc[key] = (acc[key] || 0) + n;
    return acc;
  }, {});
  const totalEntries = Object.entries(totalsByMeasure);

  // Column layout: [caret] [dimensions...] [category] [value] [measure] [actions]
  const leadingCols = 1 + dimCount;
  const totalColumns = leadingCols + 3 + 1;

  return (
    <>
      {rows.length > 0 && (
        <DimensionsGridFilterBar
          dimensionGroups={dimensionGroups}
          columns={extraFilterColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          filteredCount={filteredItems.length}
          totalCount={totalCount}
        />
      )}
      <Flex justify="flex-end" mb={2}>
        <Text fontSize="13px" color="gray.500">{t("clickRowForDetails")}</Text>
      </Flex>
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white" mb={2}>
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} pl={2} pr={0} width="32px" />
              {renderDimensionHeaderCells(organizationStructure)}
              <Th {...TH_PROPS}>{t("columns.category")}</Th>
              <Th {...TH_PROPS}>
                <Flex as="span" align="center" gap={1.5}>
                  {t("columns.consumptionValue")}
                  <ArrowsDownUp size={12} color="#718096" />
                </Flex>
              </Th>
              <Th {...TH_PROPS}>{t("columns.measure")}</Th>
              <Th py={3} px={4} width="80px" />
            </Tr>
          </Thead>
          <Tbody>
            {pageSlice.map((row, idx) => {
              const record = row.__record;
              const expandable = isExpandable(row.category);
              const isOpen = expandable && !!expandedRows[row.id];
              return (
                <React.Fragment key={row.id}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={expandable ? "pointer" : "default"}
                    _hover={expandable ? { bg: "gray.50" } : undefined}
                    onClick={() => expandable && toggleRow(row.id)}
                  >
                    <Td py={3} pl={2} pr={0}>
                      {expandable ? (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse calculation details" : "Expand calculation details"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(row.id);
                          }}
                        />
                      ) : null}
                    </Td>
                    {renderDimensionValueCells(organizationStructure, row)}
                    <Td py={3} px={4} fontSize="14px" color="gray.700">
                      {t(`categories.${row.category}`)}
                    </Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700">
                      {row.consumption?.value}
                    </Td>
                    <Td py={3} px={4} fontSize="14px" color="gray.700">{measureLabel}</Td>
                    <Td py={3} px={4} textAlign="right">
                      {!isPreview && (
                        <Flex justify="flex-end" gap={0} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            aria-label="Edit row"
                            icon={<PencilSimple size={14} />}
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            _hover={{ color: "limeShade.600", bg: "lightLime" }}
                            onClick={() => onEdit(record)}
                          />
                          <IconButton
                            aria-label="Delete row"
                            icon={<Trash size={14} />}
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            _hover={{ color: "red.500", bg: "red.50" }}
                            onClick={() => onDelete(record)}
                          />
                        </Flex>
                      )}
                    </Td>
                  </Tr>
                  {expandable && (
                    <Tr>
                      <Td colSpan={totalColumns} p={0} borderTop="none">
                        <Collapse in={isOpen} animateOpacity>
                          <Box bg="gray.50" px={6} py={4}>
                            <RowDetails row={row} />
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
            {filteredItems.length === 0 && (
              <Tr>
                <Td colSpan={totalColumns} py={8} px={4} textAlign="center">
                  <Text fontSize="14px" color="gray.400">{t("noItemsMatchFilters")}</Text>
                </Td>
              </Tr>
            )}
            {totalEntries.map(([measureId, sum], idx) => (
              <Tr
                key={`total-${measureId}`}
                bg="lightLime"
                borderTop={idx === 0 ? "1px solid" : "none"}
                borderColor="gray.200"
              >
                <Td colSpan={leadingCols + 1} py={3} px={4} fontSize="14px" fontWeight="700" color="gray.700">
                  {idx === 0 ? (isFiltered ? t("columns.filteredTotal") : t("columns.total")) : ""}
                </Td>
                <Td colSpan={2} py={3} px={4} fontSize="14px" fontWeight="700" color="gray.700" textAlign="right">
                  {sum.toLocaleString("en-GB", { maximumFractionDigits: 2 })}{" "}
                  <Text as="span" color="gray.500" fontWeight="500">{measureLabel}</Text>
                </Td>
                {/* Trailing cell for the actions column so the green band spans
                    the full table width (no gap under edit/delete). */}
                <Td py={3} px={4} />
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {filteredItems.length > 0 && (
        <Flex justifyContent={isPreview ? "center" : "start"} mb={2}>
          <Paginator
            selectedPage={safePageIndex + 1}
            numberOfPages={totalPages}
            handlePageSelection={handlePageSelection}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            totalCount={filteredItems.length}
          />
        </Flex>
      )}
    </>
  );
}

export default PurchasedElectricityTable;
